import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CONFIRM_DIALOG_MODELS,
  getConfirmDialogModel,
} from "./confirmDialogModels";
import "./confirmDialog.scss";

const WarningIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M11 2L20 18H2L11 2Z"
      stroke="#F5A623"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M11 8V12"
      stroke="#F5A623"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="11" cy="15.5" r="0.9" fill="#F5A623" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.33398 7.14706H18.6673M10.334 15.9706V10.6765M13.6673 15.9706V10.6765M15.334 19.5H8.66732C7.74684 19.5 7.00065 18.7099 7.00065 17.7353V8.02941C7.00065 7.5421 7.37375 7.14706 7.83398 7.14706H16.1673C16.6276 7.14706 17.0007 7.5421 17.0007 8.02941V17.7353C17.0007 18.7099 16.2545 19.5 15.334 19.5ZM10.334 7.14706H13.6673C14.1276 7.14706 14.5007 6.75202 14.5007 6.26471V5.38235C14.5007 4.89504 14.1276 4.5 13.6673 4.5H10.334C9.87375 4.5 9.50065 4.89504 9.50065 5.38235V6.26471C9.50065 6.75202 9.87375 7.14706 10.334 7.14706Z"
      stroke="#FF5757"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const ICON_MAP = {
  warning: WarningIcon,
  delete: DeleteIcon,
};

const ConfirmDialog = ({
  show = false,
  type,
  model,
  title,
  message,
  cancelLabel,
  confirmLabel,
  confirmTone,
  icon,
  emphasizeLastLine,
  onConfirm,
  onCancel,
  onClose,
  confirmDisabled = false,
  closeOnBackdrop = true,
  id = "confirmDialog",
}) => {
  const { t } = useTranslation();

  const resolvedModel = useMemo(() => {
    if (model) return model;
    if (type) return getConfirmDialogModel(type);
    return CONFIRM_DIALOG_MODELS.discard;
  }, [model, type]);

  const dialogTitle = title ?? resolvedModel.title;
  const dialogMessage = message ?? resolvedModel.message;
  const dialogCancelLabel = cancelLabel ?? resolvedModel.cancelLabel;
  const dialogConfirmLabel = confirmLabel ?? resolvedModel.confirmLabel;
  const dialogConfirmTone =
    confirmTone ?? resolvedModel.confirmTone ?? "primary";
  const dialogIcon = icon ?? resolvedModel.icon ?? "warning";
  const dialogEmphasizeLastLine =
    emphasizeLastLine ?? resolvedModel.emphasizeLastLine ?? false;

  const messages = Array.isArray(dialogMessage)
    ? dialogMessage
    : [dialogMessage];
  const IconComponent = ICON_MAP[dialogIcon] || WarningIcon;

  const handleClose = () => {
    if (onClose) onClose();
    else if (onCancel) onCancel();
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    if (!show) return undefined;
    document.body.classList.add("modal-open");
    const onKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show confirm-dialog-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        style={{ display: "block" }}
        onClick={closeOnBackdrop ? handleClose : undefined}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(event) => event.stopPropagation()}
        >
          <div id={id} className="confirm-dialog modal-content">
            <div className="confirm-dialog__header">
              <span className="confirm-dialog__icon">
                <IconComponent />
              </span>
              <h2 id={`${id}-title`} className="confirm-dialog__title">
                {t(dialogTitle)}
              </h2>
            </div>

            <div className="confirm-dialog__body">
              {messages.map((line, index) => {
                const isLast = index === messages.length - 1;
                const isEmphasis = dialogEmphasizeLastLine && isLast;
                return (
                  <p
                    key={`${line}-${index}`}
                    className={`confirm-dialog__message${
                      isEmphasis ? " confirm-dialog__message--emphasis" : ""
                    }`}
                  >
                    {t(line)}
                  </p>
                );
              })}
            </div>

            <div className="confirm-dialog__footer">
              <button
                type="button"
                className="confirm-dialog__btn confirm-dialog__btn--cancel"
                onClick={handleClose}
              >
                {t(dialogCancelLabel)}
              </button>
              <button
                type="button"
                disabled={confirmDisabled}
                className={`confirm-dialog__btn confirm-dialog__btn--confirm confirm-dialog__btn--${dialogConfirmTone}`}
                onClick={handleConfirm}
              >
                {t(dialogConfirmLabel)}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show confirm-dialog-backdrop" />
    </>
  );
};

export default ConfirmDialog;
